import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link as MuiLink,
  Typography
} from '@mui/material';
import {
  TrendingDown as TrendingDownIcon,
  QueryStatsOutlined as QueryStatsIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CreditProjection } from '../../types/creditPackage';
import { ManagedByReseller } from '../../types/clientAccount';

// PROYECCIÓN de saldo del portal cliente: la única cifra de «Mi cuenta» que
// mira hacia delante. Todo lo demás cuenta lo que ya pasó; esto es lo que evita
// que un cliente se entere de que se ha quedado sin créditos porque sus
// empleados dejaron de trabajar.
//
// Dos reglas que gobiernan el componente entero:
//  1. Con poco histórico NO se pinta NADA (available=false). Una media hecha
//     sobre dos días da fechas que bailan en cada recarga, y eso desgasta la
//     confianza en el resto del panel más de lo que aporta el dato.
//  2. Se etiqueta SIEMPRE como estimación. No es una factura ni una promesa:
//     es «a este ritmo», y el ritmo cambia.
//
// El cálculo vive entero en el backend (ClientAccountService.consumption_projection):
// aquí no se deriva ni un número, solo se cuenta.

const formatCredits = (value: number | string | null | undefined): string => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? String(value ?? '') : num.toFixed(2);
};

// 'YYYY-MM-DD' se ancla a medianoche LOCAL (sin el T00:00:00 JS lo lee como UTC
// y en husos negativos pinta el día anterior) — mismo criterio que CreditsSummary.
const formatDay = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const normalized = raw.includes('T') ? raw : `${raw}T00:00:00`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleDateString();
};

interface Props {
  projection: CreditProjection | null;
  managedBy?: ManagedByReseller | null;
  // Ir a comprar créditos. No se pasa en cuentas gestionadas: ahí lo accionable
  // es escribir al partner, no un catálogo que ese cliente no puede contratar.
  onBuyCredits?: () => void;
}

const CreditProjectionCard: React.FC<Props> = ({ projection, managedBy, onBuyCredits }) => {
  const { t } = useTranslation();

  // Regla 1: sin histórico suficiente, silencio. El `reason` viaja igualmente
  // para poder depurarlo desde la red sin adivinar por qué no se pinta.
  if (!projection || !projection.available) return null;

  const runsOut = formatDay(projection.runs_out_on);
  const renews = formatDay(projection.cycle_renews_at);
  const leftover = Number(projection.leftover_at_renewal ?? 0);
  const fixed = Number(projection.fixed_cycle_credits ?? 0);
  const warn = projection.runs_out_before_renewal;

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: warn ? 'warning.light' : '#e0e0e0',
        bgcolor: warn ? '#fffaf2' : 'background.paper',
        borderRadius: 2,
        mb: 2
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
          <QueryStatsIcon color={warn ? 'warning' : 'action'} fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('customer.projection.title', 'A este ritmo')}
          </Typography>
          {/* Etiqueta de ESTIMACIÓN, siempre y a la vista: no es una factura. */}
          <Chip
            size="small"
            variant="outlined"
            label={t('customer.projection.estimate', 'Estimación')}
          />
        </Box>

        {/* El ritmo, con su ventana explícita: un consumo medio sin periodo no
            se puede interpretar. */}
        <Typography variant="body2" color="text.secondary">
          {t('customer.projection.dailyAvg', 'Consumo medio: {{credits}} créditos al día (últimos {{days}} días)', {
            credits: formatCredits(projection.daily_avg),
            days: projection.window_days
          })}
        </Typography>
        {fixed > 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('customer.projection.fixedCharges', 'Cargos fijos del ciclo (almacenamiento): {{credits}} créditos', {
              credits: formatCredits(fixed)
            })}
          </Typography>
        )}

        {/* La conclusión útil, en grande: hasta cuándo llega el saldo. */}
        <Box mt={1.5} display="flex" alignItems="center" gap={1}>
          <TrendingDownIcon fontSize="small" color={warn ? 'warning' : 'action'} />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {runsOut
              ? t('customer.projection.runsOut', 'Tu saldo llega hasta el {{date}}', { date: runsOut })
              : t('customer.projection.runsOutFar', 'Tu saldo da para bastante más de un año')}
          </Typography>
        </Box>
        {runsOut && projection.days_left !== null && projection.days_left !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {t('customer.projection.daysLeft', 'unos {{days}} días', { days: projection.days_left })}
          </Typography>
        )}

        {/* Lo que sobra del plan NO «sobra»: caduca. Decirlo es lo que permite
            al cliente bajarse de paquete y pagar menos. */}
        {renews && leftover > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('customer.projection.leftover', 'Al cerrar el ciclo el {{date}} te sobrarán unos {{credits}} créditos del plan, y esos caducan.', {
              date: renews,
              credits: formatCredits(leftover)
            })}
          </Typography>
        )}

        {/* Aviso accionable: se agota ANTES de la renovación. La acción depende
            de quién gestiona la cuenta — el patrón de todo el portal. */}
        {warn && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {managedBy
              ? t('customer.projection.warnManaged', 'A este ritmo te quedarás sin créditos antes de la renovación del {{date}}. Pídele más a {{name}} para que tus empleados no dejen de trabajar.', {
                  date: renews ?? '',
                  name: managedBy.name
                })
              : t('customer.projection.warn', 'A este ritmo te quedarás sin créditos antes de la renovación del {{date}}.', {
                  date: renews ?? ''
                })}
            <Box mt={1}>
              {managedBy?.contact_email ? (
                <MuiLink href={`mailto:${managedBy.contact_email}`} underline="hover">
                  {managedBy.contact_email}
                </MuiLink>
              ) : (
                onBuyCredits && (
                  <Button size="small" variant="outlined" color="warning" onClick={onBuyCredits} sx={{ textTransform: 'none' }}>
                    {t('customer.projection.buyCredits', 'Comprar créditos')}
                  </Button>
                )
              )}
            </Box>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default CreditProjectionCard;
